import { zodResolver } from "@hookform/resolvers/zod";
import { ChangeEvent, Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import { FaArrowUp } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { z } from "zod";

import { useMain } from "@/contexts/mainContext";
import { QueroUmaPerucaDTOStep5 } from "@/dtos/QueroUmaPerucaDTO";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const MIN_FILES = 2;
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25 MB

const ALLOWED_FILE_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const lastFormSchema = z.object({
  comoficousabendo: z
    .string()
    .min(1, "Não esqueça de nos contar como você conheceu a Cabelegria!"),
  terms: z.boolean().optional(),
});

type LastFormData = z.infer<typeof lastFormSchema>;

export function QueroUmaPerucaFormStep5() {
  const { progress, setProgress, setFormValues } = useMain();

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LastFormData>({
    resolver: zodResolver(lastFormSchema),
    mode: "all",
  });

  function handleInputMultipleFileChange({
    currentTarget: { files },
  }: ChangeEvent<HTMLInputElement>) {
    if (!files?.length) {
      return;
    }

    const selectedFiles = Array.from(files);

    const nextFiles = [...uploadedFiles, ...selectedFiles];

    // Máximo de arquivos
    if (nextFiles.length > MAX_FILES) {
      setFileError(`Você pode enviar no máximo ${MAX_FILES} arquivos.`);

      return;
    }

    // Tipos permitidos
    const invalidType = selectedFiles.find(
      (file) => !ALLOWED_FILE_TYPES.includes(file.type),
    );

    if (invalidType) {
      setFileError(
        "Formato não permitido. Envie arquivos PDF, JPG, JPEG ou PNG.",
      );

      return;
    }

    // Máximo de 10 MB por arquivo
    const oversizedFile = selectedFiles.find(
      (file) => file.size > MAX_FILE_SIZE,
    );

    if (oversizedFile) {
      setFileError("Cada arquivo pode ter no máximo 10 MB.");

      return;
    }

    // Máximo de 25 MB no total
    const totalSize = nextFiles.reduce((total, file) => total + file.size, 0);

    if (totalSize > MAX_TOTAL_SIZE) {
      setFileError("O tamanho total dos arquivos não pode ultrapassar 25 MB.");
    }

    setUploadedFiles(nextFiles);
    setFileError("");
  }

  function handleDeleteFiles(fileIndex: number) {
    setUploadedFiles((existing) =>
      existing.filter((_, index) => index !== fileIndex),
    );

    setFileError("");
  }

  async function SendQueroUmaPerucaFormData(values: QueroUmaPerucaDTOStep5) {
    if (uploadedFiles.length < MIN_FILES) {
      setFileError(`Por favor, anexe pelo menos ${MIN_FILES} documentos.`);

      return;
    }

    setFileError("");

    setFormValues({
      ...values,
      docs: uploadedFiles,
    });

    setProgress(progress + 1);
  }

  return (
    <>
      <h2 className="font-roboto text-2xl font-bold text-pink-650 max-[1279px]:text-center max-[480px]:text-lg">
        Último passo:
      </h2>

      <form
        className="mt-8 flex flex-col items-center justify-center gap-8 max-[1024px]:w-full max-[480px]:gap-4"
        onSubmit={handleSubmit(SendQueroUmaPerucaFormData)}
        encType="multipart/form-data"
      >
        <div className="flex w-full gap-4 max-[540px]:flex-col">
          <div className="w-full">
            <label
              htmlFor="foto-documentos"
              className="flex h-20 cursor-pointer items-center justify-center gap-4 rounded-md border border-solid border-pink-650 bg-white p-12 font-roboto text-xs font-medium text-pink-650
              max-[480px]:h-[150px]"
            >
              <FaArrowUp className="max-[600px]:hidden" size={20} />

              <span className="block leading-4 max-[540px]:text-center">
                Clique <strong>AQUI</strong> e anexe os seguintes documentos:{" "}
                <br />
                - RG/CPF
                <br />
                - Laudo Médico
                <br />
                - Comprovante de Quimioterapia
                <br />- Uma foto sua de referência, se desejar
              </span>
            </label>

            <Input
              type="file"
              id="foto-documentos"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={handleInputMultipleFileChange}
              multiple
              className="focus:ring-red border border-solid border-pink-650 bg-transparent font-roboto font-medium capitalize text-pink-650 outline-none placeholder:font-roboto placeholder:normal-case placeholder:text-pink-650"
            />

            {fileError && (
              <p className="pt-1 text-left text-sm text-red-600">{fileError}</p>
            )}

            {uploadedFiles.length > 0 && (
              <div className="pt-2">
                <span className="font-roboto text-base font-normal text-pink-650">
                  <strong>Arquivos: </strong>
                </span>

                <p className="flex flex-col gap-2 pt-2 font-roboto text-base font-normal text-pink-650 max-[320px]:text-xs">
                  {uploadedFiles.map((file, index) => {
                    return (
                      <Fragment key={`${file.name}-${file.size}-${index}`}>
                        <div className="flex gap-2">
                          <span
                            className="inline-block h-5 w-5 cursor-pointer bg-red-500"
                            onClick={() => handleDeleteFiles(index)}
                          >
                            <IoClose color="white" size={20} />
                          </span>

                          {file.name.length > 14
                            ? file.name.slice(11)
                            : file.name}
                        </div>
                      </Fragment>
                    );
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        <Textarea
          placeholder="Nos conte como ficou sabendo da Cabelegria:"
          {...register("comoficousabendo")}
          className={
            errors.comoficousabendo?.message
              ? "focus:ring-red resize-none border border-solid border-red-600 bg-transparent font-roboto font-medium normal-case text-red-600 outline-none placeholder:font-roboto placeholder:normal-case placeholder:text-red-600"
              : "focus:ring-red resize-none border border-solid border-pink-650 bg-transparent font-roboto font-medium normal-case text-pink-650 outline-none placeholder:font-roboto placeholder:normal-case placeholder:text-pink-650"
          }
        />

        {errors.comoficousabendo?.message && (
          <p className="pt-1 text-left text-sm text-red-600">
            {errors.comoficousabendo.message}
          </p>
        )}

        <div className="flex w-full items-center gap-2">
          <input
            type="checkbox"
            {...register("terms")}
            className="h-5 w-5 appearance-none border-[1px] border-pink-650 checked:border-pink-650 checked:bg-pink-650 max-[480px]:h-6 max-[480px]:w-8"
          />

          <p className="font-roboto text-sm font-normal text-pink-650">
            <strong>
              Aceito receber por e-mail as novidades da Cabelegria
            </strong>
          </p>
        </div>

        <Button
          type="submit"
          variant="transparent"
          disabled={uploadedFiles.length < MIN_FILES}
          className="w-1/4 border-pink-650 bg-pink-650 font-roboto text-sm font-medium text-white max-[540px]:w-full"
        >
          Finalizar
        </Button>
      </form>
    </>
  );
}
